/**
 * hub-adulto.ts — O hub do público ADULTO (F2 do estudo games-estrategia, P-166).
 *
 * Mesmas 57 atividades, outra porta: agrupadas pelas competências de
 * matemática da matriz do ENCCEJA (Inep), com título de gente grande,
 * sem faixa de idade, sem mascote e sem paisagem infantil.
 *
 * Este mapa espelha o seed de `jogo_habilidade` do monolito (migração 301).
 * Mudou lá, muda aqui. A rota é #/adulto (e #/adulto/{atividade} abre direto).
 */

import type { ActivityModule } from './activity';
import { getHighestLevel } from './score';
import { audioManager } from './audio';

interface TreinoItem {
  id: string;
  titulo: string;
}

interface Competencia {
  codigo: string;
  nome: string;
  descricao: string;
  itens: TreinoItem[];
}

export const COMPETENCIAS: Competencia[] = [
  {
    codigo: 'F1',
    nome: 'Números e operações',
    descricao: 'As quatro operações, comparação e ordem. A base de todo o resto.',
    itens: [
      { id: 'algebra_plus', titulo: 'Adição' },
      { id: 'algebra_minus', titulo: 'Subtração' },
      { id: 'algebra_by', titulo: 'Multiplicação' },
      { id: 'algebra_div', titulo: 'Divisão' },
      { id: 'vertical_addition', titulo: 'Conta armada: somar' },
      { id: 'vertical_subtraction', titulo: 'Conta armada: subtrair' },
      { id: 'comparator', titulo: 'Comparar números' },
      { id: 'ordering_numbers', titulo: 'Ordenar números' },
    ],
  },
  {
    codigo: 'F3',
    nome: 'Grandezas e medidas',
    descricao: 'Dinheiro, troco, horas e medidas do dia a dia.',
    itens: [
      { id: 'money', titulo: 'Pagar uma compra' },
      { id: 'money_back', titulo: 'Conferir o troco' },
      { id: 'money_cents', titulo: 'Pagar com centavos' },
      { id: 'money_back_cents', titulo: 'Troco com centavos' },
      { id: 'clockgame', titulo: 'Ler e marcar o relógio' },
      { id: 'calendar', titulo: 'Calendário e prazos' },
      { id: 'scalesboard', titulo: 'Equilibrar a balança' },
      { id: 'graduated_line_use', titulo: 'Ler a régua' },
    ],
  },
  {
    codigo: 'F4',
    nome: 'Proporcionalidade',
    descricao: 'Frações e divisão em partes iguais. O caminho para a porcentagem.',
    itens: [
      { id: 'fractions_find', titulo: 'Ler frações' },
      { id: 'fractions_create', titulo: 'Montar frações e porcentagens' },
      { id: 'share', titulo: 'Dividir em partes iguais' },
    ],
  },
  {
    codigo: 'F7',
    nome: 'Matemática no cotidiano',
    descricao: 'Raciocínio rápido com as operações misturadas.',
    itens: [
      { id: 'guess24', titulo: 'Fechar 24' },
      { id: 'calcudoku', titulo: 'Grade de contas' },
      { id: 'guessnumber', titulo: 'Adivinhar o número' },
    ],
  },
  {
    codigo: 'M1',
    nome: 'Números decimais (ensino médio)',
    descricao: 'Décimos e centésimos: o que aparece em preço, medida e nota.',
    itens: [
      { id: 'learn_decimals', titulo: 'Entender decimais' },
      { id: 'learn_decimals_additions', titulo: 'Somar decimais' },
      { id: 'learn_decimals_subtractions', titulo: 'Subtrair decimais' },
    ],
  },
];

export function mountHubAdulto(root: HTMLElement, activities: ActivityModule[]): void {
  root.innerHTML = '';
  const byId = new Map(activities.map((a) => [a.id, a]));

  const page = document.createElement('div');
  page.className = 'hub-page hub-page--adulto';

  const grupos = COMPETENCIAS.map((c) => {
    const cards = c.itens
      .map((item) => {
        const act = byId.get(item.id);
        if (!act) return '';
        const progress = getHighestLevel(act.id);
        return `
          <button class="treino-card" type="button" data-id="${act.id}" role="listitem">
            <img class="treino-card__icon" src="${act.iconPath}" alt="" aria-hidden="true" />
            <span class="treino-card__body">
              <span class="treino-card__title">${item.titulo}</span>
              <span class="treino-card__progress">Nível ${Math.min(progress + 1, act.totalLevels)} de ${act.totalLevels}</span>
            </span>
          </button>`;
      })
      .join('');
    if (!cards) return '';
    return `
      <section class="treino-grupo" aria-label="${c.codigo} ${c.nome}">
        <h2 class="treino-grupo__title"><span class="treino-grupo__codigo">${c.codigo}</span> ${c.nome}</h2>
        <p class="treino-grupo__desc">${c.descricao}</p>
        <div class="treino-grupo__grid" role="list">${cards}</div>
      </section>`;
  }).join('');

  page.innerHTML = `
    <header class="hub-header hub-header--adulto">
      <h1>Treinos de matemática</h1>
      <p class="hub-subtitle">Pratique no seu ritmo, sem cadastro. Organizado pelas competências da prova do ENCCEJA.</p>
      <button class="hub-mute" type="button" aria-label="Ligar/desligar som">${audioManager.isMuted() ? '🔇 Som off' : '🔊 Som on'}</button>
    </header>
    <main class="hub-adulto-main">${grupos}</main>
    <footer class="hub-footer">
      <p><a href="#/">Modo criança</a> · Adaptado de <a href="https://gcompris.net/" target="_blank" rel="noopener">GCompris</a> (KDE) · <a href="creditos.html">Créditos e licenças</a></p>
    </footer>
  `;

  page.querySelectorAll<HTMLElement>('.treino-card').forEach((card) => {
    card.addEventListener('click', () => {
      audioManager.play('click');
      window.location.hash = `/adulto/${card.dataset.id}`;
    });
  });

  const muteBtn = page.querySelector<HTMLButtonElement>('.hub-mute')!;
  muteBtn.addEventListener('click', () => {
    audioManager.setMuted(!audioManager.isMuted());
    muteBtn.textContent = audioManager.isMuted() ? '🔇 Som off' : '🔊 Som on';
  });

  root.appendChild(page);
}
